import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "@dr.pogodin/react-helmet";
import Navbar from "./navbar";
import Footer from "./footer";
import "../css/App.css";

const CheckIcon = () => (
    <svg viewBox="0 0 24 24" width="34" height="34" fill="none" aria-hidden="true">
        <path d="M20 6.5 9.5 17 4 11.5" stroke="#0f3650" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DashIcon = () => (
    <svg viewBox="0 0 24 24" width="34" height="34" fill="none" aria-hidden="true">
        <path d="M6 12h12" stroke="#0f3650" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
);

function CheckoutResult({ tone, badge, title, body, primary, secondary, docTitle }) {
    return (
        <div className="checkout-page">
            <Helmet><title>{docTitle}</title></Helmet>
            <Navbar />

            <div className="checkout-result">
                <div className="checkout-card">
                    <div className={`checkout-badge ${tone}`}>{badge}</div>
                    <h1 className="checkout-title">{title}</h1>
                    <p className="checkout-body">{body}</p>
                    <div className="checkout-actions">
                        {primary && (
                            <Link to={primary.to} className="checkout-btn primary">{primary.label}</Link>
                        )}
                        {secondary && (
                            <Link to={secondary.to} className="checkout-btn ghost">{secondary.label}</Link>
                        )}
                    </div>
                </div>
            </div>

            <Footer />

            <style>{`
                .checkout-page {
                    display: flex;
                    flex-direction: column;
                    min-height: 100vh;
                }
                .checkout-result {
                    flex: 1 1 auto;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    padding: 64px 20px 84px;
                    background: #fbf5ea;
                }
                .checkout-card {
                    width: 100%;
                    max-width: 480px;
                    background: #ffffff;
                    border: 1px solid #ecdcbf;
                    border-top: 4px solid #c08d3b;
                    border-radius: 18px;
                    box-shadow: 0 18px 46px rgba(15, 54, 80, 0.12);
                    padding: 44px 40px 40px;
                    text-align: center;
                }
                .checkout-badge {
                    width: 76px;
                    height: 76px;
                    border-radius: 999px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    margin: 0 auto 22px;
                }
                .checkout-badge.success { background: #f6e6c4; border: 1px solid #e6c98d; }
                .checkout-badge.neutral { background: #e7edf1; border: 1px solid #cdd9e0; }
                .checkout-title {
                    font-family: 'EB Garamond', Georgia, serif;
                    font-size: 30px;
                    line-height: 1.2;
                    color: #0f3650;
                    margin: 0 0 12px;
                    font-weight: 600;
                }
                .checkout-body {
                    color: #4a5763;
                    font-size: 16px;
                    line-height: 1.6;
                    margin: 0 auto 26px;
                    max-width: 360px;
                }
                .checkout-actions {
                    display: flex;
                    flex-wrap: wrap;
                    gap: 10px;
                    justify-content: center;
                }
                .checkout-btn {
                    display: inline-block;
                    padding: 12px 22px;
                    border-radius: 10px;
                    font-weight: 700;
                    font-size: 15px;
                    text-decoration: none;
                    transition: transform 0.05s ease, background 0.15s ease;
                }
                .checkout-btn:active { transform: translateY(1px); }
                .checkout-btn.primary { background: #f1b249; color: #0f3650; }
                .checkout-btn.primary:hover { background: #e7a733; }
                .checkout-btn.ghost { background: transparent; color: #0f3650; border: 1px solid #c9d3da; }
                .checkout-btn.ghost:hover { background: #f1f4f6; }

                @media (max-width: 520px) {
                    .checkout-result { padding: 40px 16px 60px; }
                    .checkout-card { padding: 34px 22px 30px; }
                    .checkout-title { font-size: 25px; }
                    .checkout-btn { width: 100%; }
                }
            `}</style>
        </div>
    );
}

export function CheckoutSuccess() {
    const [params] = useSearchParams();
    const kind = params.get("kind");
    const recurring = params.get("recurring") === "1";

    let title = "Thank you!";
    let body = "Your payment was received. A confirmation has been emailed to you.";
    let primary = { to: "/", label: "Return home" };
    let secondary = null;

    if (kind === "donation") {
        title = recurring ? "Thank you for your monthly gift" : "Thank you for your gift";
        body = recurring
            ? "Your recurring donation to SOLE is set up, and a receipt has been emailed to you. You can change or cancel it anytime by contacting us."
            : "Your donation to SOLE has been received, and a receipt is on its way to your inbox. Your generosity forms and sends Catholic evangelists across the country.";
        primary = { to: "/", label: "Return home" };
        secondary = { to: "/give", label: "Give again" };
    } else if (kind === "candle") {
        title = "Your order is confirmed";
        body = "Thank you for ordering the Altar Flame candle. We’ll email you shortly with pickup or shipping details.";
        primary = { to: "/", label: "Return home" };
        secondary = { to: "/candle", label: "Back to the candle" };
    } else if (kind === "shirt") {
        title = "Your order is confirmed";
        body = "Thank you for your order. We’ll email you with the details and let you know when it’s ready.";
        primary = { to: "/", label: "Return home" };
    }

    return (
        <CheckoutResult
            tone="success"
            badge={<CheckIcon />}
            docTitle="Thank You – SOLE"
            title={title}
            body={body}
            primary={primary}
            secondary={secondary}
        />
    );
}

export function CheckoutCancel() {
    const [params] = useSearchParams();
    const kind = params.get("kind");

    let title = "Checkout canceled";
    let body = "No charge was made. If this was a mistake, you can try again.";
    let primary = { to: "/", label: "Return home" };
    let secondary = null;

    if (kind === "donation") {
        title = "Donation canceled";
        body = "No charge was made. If you’d still like to support SOLE, you can start again whenever you’re ready.";
        primary = { to: "/give", label: "Return to giving" };
        secondary = { to: "/", label: "Return home" };
    } else if (kind === "candle") {
        title = "Order canceled";
        body = "No charge was made. The Altar Flame candle is still available whenever you’d like to order.";
        primary = { to: "/candle", label: "Back to the candle" };
        secondary = { to: "/", label: "Return home" };
    } else if (kind === "shirt") {
        title = "Order canceled";
        body = "No charge was made. You can start your order again anytime.";
        primary = { to: "/", label: "Return home" };
    }

    return (
        <CheckoutResult
            tone="neutral"
            badge={<DashIcon />}
            docTitle="Checkout Canceled – SOLE"
            title={title}
            body={body}
            primary={primary}
            secondary={secondary}
        />
    );
}
